import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import NormalHeader from "../NormalHeader/NormalHeader";
import classes from "./NormalMode.module.scss";
import classNames from "classnames";
import { TerminalMode } from "../../../../../pagesMain/TerminalPage/TerminalPage";
import { OrderUnitsType } from "../../../../../types/Terminal/TerminalTypes";
import { useGetTicket } from "../../../../../hooks/TerminalServices/useGetTicket";
import TerminalModal from "../../../Modals/TerminalModal/TerminalModal";
import PrintedTicketModal from "../../../Modals/ModalPrintedTicket/ModalPrintedTicket";
import { isAfterPrintTicketEnabled } from "../../../../../constants/API";
import {
  TerminalAppearanceResponse,
  TerminalBackgroundMedia,
} from "../../../../../types/Admin/TerminalAppearanceTypes";
import { getMediaBackgroundSize } from "../../../../../utils/mediaBackground";
import { useTerminalAutoReturnHome } from "../../../../../hooks/TerminalServices/useTerminalAutoReturnHome";
import { useTerminalScreensaver } from "../../../../../hooks/TerminalServices/useTerminalScreensaver";
import { TerminalScreensaver } from "../../../TerminalScreensaver/TerminalScreensaver";
import { useChildrenModeController } from "../../../../../hooks/TerminalServices/useChildrenModeController";
import { useChildrenModeAgeDetection } from "../../../../../hooks/TerminalServices/useChildrenModeAgeDetection";
import {
  createChildrenTileColorMap,
  resolveChildrenModeVisualSettings,
} from "../../../../../utils/Terminal/childrenModeThemes";
import {
  ChildrenMode,
} from "../../ChildrenMode/ChildrenMode";
import { NormalModeView } from "./NormalModeView";
import {
  buildChildrenAppearanceStyle,
  buildNormalAppearanceStyle,
  checkIsCategory,
  elementsLocalization,
  getMediaObjectFit,
  getServiceTopicId,
  getExtraInputTopic,
  getTicketName,
  getVideoMimeType,
  isPrivilegeTile,
  languageType,
  resolveChildrenBackground,
  resolveNormalBackground,
  TerminalTicketItem,
  transformOrderUnitsToArr,
} from "./terminalModeShared";
import TerminalMisScanModal from "../../../Modals/TerminalMisScanModal/TerminalMisScanModal";
import TerminalExtraDataModal from "../../../Modals/TerminalExtraDataModal/TerminalExtraDataModal";
import { useMisTerminalSession } from "../../../../../hooks/TerminalServices/useMisTerminalSession";
import { TopicsType } from "../../../../../types/Terminal/TerminalTypes";

export type { languageType };

type NormalModePropsType = {
  setMode: (mode: TerminalMode) => void;
  orderUnitsData: OrderUnitsType;
  tickerText?: string;
  appearance?: TerminalAppearanceResponse;
  previewChildrenModeOverride?: boolean;
  isPreviewMode?: boolean;
};

export const NormalMode: FC<NormalModePropsType> = ({
  setMode,
  tickerText,
  orderUnitsData,
  appearance,
  previewChildrenModeOverride,
  isPreviewMode = false,
}) => {
  const [language, setLanguage] = useState<languageType>("ru");
  const [currentSectionsServices, setCurrentSectionsServices] = useState<
    TerminalTicketItem[]
  >([]);
  const [history, setHistory] = useState<{
    [key: string]: TerminalTicketItem[];
  }>({});
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [activeModal, setActiveModal] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState<number>();
  const [extraInputTarget, setExtraInputTarget] = useState<TopicsType | null>(
    null,
  );
  const [pendingExtraData, setPendingExtraData] = useState<string | undefined>();
  const [childrenTileColors, setChildrenTileColors] = useState<
    Map<string, string>
  >(new Map());

  const childrenModeSettings = appearance?.settings?.childrenMode;
  const childrenVisual = useMemo(
    () => resolveChildrenModeVisualSettings(childrenModeSettings),
    [
      childrenModeSettings?.hideIcons,
      childrenModeSettings?.showStandardModeIcons,
      childrenModeSettings?.decorationType,
      childrenModeSettings?.presetThemeId,
      childrenModeSettings?.buttonShape,
      childrenModeSettings?.backgroundFitMode,
      childrenModeSettings?.backgroundOffsetByHeader,
      childrenModeSettings?.activeBackgroundId,
    ],
  );
  const presetThemeId = childrenModeSettings?.presetThemeId;

  const endSessionChildrenModeRef = useRef<() => void>(() => {});
  const orderUnitsFingerprintRef = useRef("");
  const currentIndexRef = useRef(currentIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const [headerHeightPx, setHeaderHeightPx] = useState(120);
  currentIndexRef.current = currentIndex;

  const {
    scanTarget,
    scanError,
    isConfirming,
    confirmation,
    privilegeBanner,
    allowsDigitalCode,
    allowsBarcode,
    localTest,
    openScan,
    closeScan,
    submitScan,
    clearConfirmation,
  } = useMisTerminalSession(language, orderUnitsData?.misConfig);

  const { getTicket, isLoadingGetTicket } = useGetTicket(() => {
    clearConfirmation();
    setPendingExtraData(undefined);
    setExtraInputTarget(null);
    if (currentIndex > 0) {
      setCurrentIndex(0);
      setCurrentSectionsServices(history[0]);
    }
    endSessionChildrenModeRef.current();
  }, orderUnitsData?.terminalIp);

  const {
    isChildrenMode: controllerChildrenMode,
    showChildrenModeButton,
    toggleSessionChildrenMode,
    enableSessionChildrenMode,
    endSessionChildrenMode,
    switchByAgeEnabled,
    ageThreshold,
  } = useChildrenModeController({
    childrenModeSettings,
    currentIndex,
    activeModal: activeModal || Boolean(scanTarget) || Boolean(extraInputTarget),
    isLoadingGetTicket,
  });

  endSessionChildrenModeRef.current = endSessionChildrenMode;

  const isChildrenMode =
    typeof previewChildrenModeOverride === "boolean"
      ? previewChildrenModeOverride
      : controllerChildrenMode;

  const handleChildrenModeDetected = useCallback(
    (value: boolean) => {
      if (value) {
        enableSessionChildrenMode();
      }
    },
    [enableSessionChildrenMode],
  );

  const { videoRef } = useChildrenModeAgeDetection({
    enabled: switchByAgeEnabled && !isPreviewMode,
    ageThreshold,
    isChildrenMode,
    setIsChildrenMode: handleChildrenModeDetected,
    isPreviewMode,
  });

  useEffect(() => {
    if (!isChildrenMode) {
      setChildrenTileColors(new Map());
      return;
    }
    setChildrenTileColors(
      createChildrenTileColorMap(
        currentSectionsServices.map((ticket) => ticket.id),
      ),
    );
  }, [isChildrenMode, currentSectionsServices]);

  useEffect(() => {
    setActiveModal(isLoadingGetTicket);
  }, [isLoadingGetTicket]);

  useEffect(() => {
    if (!orderUnitsData) {
      return;
    }

    const fingerprint = JSON.stringify(orderUnitsData);
    if (fingerprint === orderUnitsFingerprintRef.current) {
      return;
    }
    orderUnitsFingerprintRef.current = fingerprint;

    setCurrentSectionsServices(transformOrderUnitsToArr(orderUnitsData));

    if (currentIndexRef.current > 0) {
      setCurrentIndex(0);
    }
  }, [orderUnitsData]);

  const requestTicket = (
    topicId: number,
    extraData?: string,
    confirmationId?: string,
  ) => {
    setActiveTicketId(topicId);
    getTicket({
      topicId,
      confirmationId: confirmationId ?? confirmation?.confirmationId,
      extraData,
    });
  };

  const handleCategoryClick = (item: TerminalTicketItem) => {
    if (isPrivilegeTile(item)) {
      openScan({
        privilegeTypeId: item.privilegeTypeId,
        misCode: item.misCode,
        privilegeKind: item.privilegeKind,
        name: getTicketName(item, language),
        scanCard: item.scanCard,
      });
      return;
    }
    if (checkIsCategory(item)) {
      setHistory((prev) => {
        const newHistory = { ...prev };
        newHistory[currentIndex] = currentSectionsServices;
        return newHistory;
      });
      setCurrentSectionsServices([
        ...(item.topics || []),
        ...(item.subCategories || []),
      ]);
      setCurrentIndex((prev) => prev + 1);
    } else {
      const topicId = getServiceTopicId(item);
      if (topicId == null) return;
      setPendingExtraData(undefined);
      const extraTopic = getExtraInputTopic(item);
      if (extraTopic) {
        setExtraInputTarget(extraTopic);
        return;
      }
      if (
        !confirmation &&
        orderUnitsData?.misConfig?.benefitsTerminalMode === "SCAN_ON_SERVICE" &&
        orderUnitsData?.misConfig?.benefitsEffective
      ) {
        openScan({
          privilegeKind: "BENEFIT",
          name: getTicketName(item, language),
          scanCard: true,
          scanOnService: true,
          topicId,
        });
        return;
      }
      requestTicket(topicId);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setCurrentSectionsServices(history[currentIndex - 1]);
    }
  };

  const autoReturnHomeIdleSeconds =
    appearance?.settings?.autoReturnHome?.idleSeconds ?? 0;
  const screensaverMedia = appearance?.screensaverMedia ?? [];
  const screensaverIdleSeconds =
    appearance?.settings?.screensaverIdleSeconds ?? 120;
  const screensaverImageSeconds =
    appearance?.settings?.screensaverImageSeconds ?? 8;
  const { isScreensaverActive, dismissScreensaver } = useTerminalScreensaver({
    idleSeconds: screensaverIdleSeconds,
    hasMedia: screensaverMedia.length > 0,
    enabled: !isPreviewMode,
  });
  const isOnHomePage =
    currentIndex === 0 && !isChildrenMode && !activeModal && !scanTarget && !extraInputTarget;

  const returnToHome = useCallback(() => {
    closeScan();
    clearConfirmation();
    setExtraInputTarget(null);
    setPendingExtraData(undefined);
    setActiveModal(false);
    setActiveTicketId(undefined);
    if (orderUnitsData) {
      setCurrentIndex(0);
      setHistory({});
      setCurrentSectionsServices(transformOrderUnitsToArr(orderUnitsData));
    }
    endSessionChildrenMode();
  }, [orderUnitsData, endSessionChildrenMode, closeScan, clearConfirmation]);

  useTerminalAutoReturnHome({
    idleSeconds: autoReturnHomeIdleSeconds,
    isOnHomePage,
    onReturnHome: returnToHome,
    enabled: !isPreviewMode && !isScreensaverActive,
  });

  useEffect(() => {
    if (!isScreensaverActive) return;
    returnToHome();
  }, [isScreensaverActive, returnToHome]);

  useEffect(() => {
    const headerWrapEl = headerContainerRef.current;
    const containerEl = containerRef.current;
    if (!headerWrapEl || !containerEl) return;

    const syncHeaderHeight = () => {
      const headerEl =
        (headerWrapEl.firstElementChild as HTMLElement | null) ?? headerWrapEl;
      const height = Math.round(
        headerEl.getBoundingClientRect().height || headerEl.offsetHeight || 0,
      );
      if (!height) return;
      setHeaderHeightPx(height);
      containerEl.style.setProperty("--terminal-header-height", `${height}px`);
    };

    syncHeaderHeight();
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(headerWrapEl);
    if (headerWrapEl.firstElementChild) {
      observer.observe(headerWrapEl.firstElementChild);
    }
    return () => observer.disconnect();
  }, [appearance?.settings?.header, appearance?.settings?.logo]);

  const activeBackground = useMemo<TerminalBackgroundMedia | null>(() => {
    if (isChildrenMode) {
      return resolveChildrenBackground(appearance, childrenVisual);
    }
    return resolveNormalBackground(appearance);
  }, [appearance, isChildrenMode, childrenVisual]);

  const greetingTitle =
    appearance?.settings?.greeting?.title?.[language] ||
    elementsLocalization.title[language];
  const greetingSubTitle =
    appearance?.settings?.greeting?.subTitle?.[language] ||
    elementsLocalization.subTitle[language];
  const isGreetingEnabled = appearance?.settings?.greeting?.isEnabled ?? true;
  const showGreeting = isGreetingEnabled;

  const appearanceStyle = useMemo(() => {
    if (isChildrenMode) {
      return buildChildrenAppearanceStyle(appearance, childrenVisual);
    }
    return buildNormalAppearanceStyle(appearance);
  }, [appearance, isChildrenMode, childrenVisual]);

  const useCategoryGrid = useMemo(() => {
    if (currentSectionsServices.length === 0) return false;
    const hasCategory = currentSectionsServices.some((item) =>
      checkIsCategory(item),
    );
    const hasService = currentSectionsServices.some(
      (item) => !checkIsCategory(item),
    );
    return hasCategory && !hasService;
  }, [currentSectionsServices]);

  const hasBackground = Boolean(activeBackground);
  const isBackgroundOffsetByHeader = isChildrenMode
    ? childrenVisual.backgroundOffsetByHeader
    : (appearance?.settings?.backgroundOffsetByHeader ?? false);
  const backgroundFitMode = isChildrenMode
    ? childrenVisual.backgroundFitMode
    : (appearance?.settings?.backgroundFitMode ?? "cover");

  const hideChildrenModeToggle =
    typeof previewChildrenModeOverride === "boolean";

  const backgroundOffsetStyle = isBackgroundOffsetByHeader
    ? {
        top: headerHeightPx,
        right: 0,
        bottom: "auto" as const,
        left: 0,
        height: `calc(100% - ${headerHeightPx}px)`,
        transform: "none",
      }
    : undefined;

  return (
    <div
      ref={containerRef}
      className={classNames(classes.normalModeContainer, {
        [classes.withBackground]: hasBackground,
        [classes.childrenMode]: isChildrenMode,
        [classes.childrenModePlainBackground]: isChildrenMode && !hasBackground,
      })}
      style={{
        ...appearanceStyle,
        ["--terminal-header-height" as string]: `${headerHeightPx}px`,
        ...(isChildrenMode && !hasBackground
          ? { backgroundColor: childrenVisual.terminalBackgroundColor }
          : {}),
      }}
    >
      {activeBackground?.mediaType === "IMAGE" && (
        <div
          className={classNames(classes.backgroundLayer, {
            [classes.backgroundOffsetHeader]: isBackgroundOffsetByHeader,
          })}
          style={{
            backgroundImage: `url(${activeBackground.url})`,
            backgroundSize: getMediaBackgroundSize(backgroundFitMode, 100),
            ...(isBackgroundOffsetByHeader
              ? { backgroundPosition: "center top" }
              : {}),
            ...backgroundOffsetStyle,
          }}
        />
      )}
      {activeBackground?.mediaType === "VIDEO" && (
        <video
          className={classNames(classes.backgroundVideo, {
            [classes.backgroundOffsetHeader]: isBackgroundOffsetByHeader,
          })}
          style={{
            objectFit: getMediaObjectFit(backgroundFitMode),
            ...(isBackgroundOffsetByHeader
              ? { objectPosition: "center top" }
              : {}),
            ...backgroundOffsetStyle,
          }}
          autoPlay
          loop
          muted
          playsInline
        >
          <source
            src={activeBackground.url}
            type={getVideoMimeType(activeBackground.url)}
          />
        </video>
      )}
      {activeModal && (
        <TerminalModal
          activeModal={activeModal}
          setActiveModal={setActiveModal}
        />
      )}
      <div className={classes.contentLayer}>
        <div ref={headerContainerRef} className={classes.headerContainer}>
          <NormalHeader
            language={language}
            setLanguage={setLanguage}
            setMode={setMode}
            appearance={appearance}
          />
        </div>
        <main className={classes.normalModeMain}>
          {isAfterPrintTicketEnabled && (
            <PrintedTicketModal
              activeModal={true}
              setActiveModal={setActiveModal}
            />
          )}

          {privilegeBanner && (
            <div className={classes.privilegeBanner}>
              <span>{privilegeBanner}</span>
              <button
                type="button"
                className={classes.privilegeBannerClose}
                onClick={clearConfirmation}
                aria-label="Закрыть"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6 L18 18 M18 6 L6 18" />
                </svg>
              </button>
            </div>
          )}

          {!isChildrenMode && (
            <NormalModeView
              language={language}
              currentSectionsServices={currentSectionsServices}
              currentIndex={currentIndex}
              activeModal={activeModal}
              isLoadingGetTicket={isLoadingGetTicket}
              activeTicketId={activeTicketId}
              appearance={appearance}
              useCategoryGrid={useCategoryGrid}
              showChildrenModeButton={showChildrenModeButton}
              hideChildrenModeToggle={hideChildrenModeToggle}
              showGreeting={showGreeting}
              greetingTitle={greetingTitle}
              greetingSubTitle={greetingSubTitle}
              onBack={handleBack}
              onToggleChildrenMode={toggleSessionChildrenMode}
              onCategoryClick={handleCategoryClick}
            />
          )}

          {isChildrenMode && (
            <ChildrenMode
              language={language}
              currentSectionsServices={currentSectionsServices}
              currentIndex={currentIndex}
              activeModal={activeModal}
              isLoadingGetTicket={isLoadingGetTicket}
              activeTicketId={activeTicketId}
              appearance={appearance}
              childrenVisual={childrenVisual}
              presetThemeId={presetThemeId}
              childrenTileColors={childrenTileColors}
              useCategoryGrid={useCategoryGrid}
              showChildrenModeButton={showChildrenModeButton}
              hideChildrenModeToggle={hideChildrenModeToggle}
              showGreeting={showGreeting}
              greetingTitle={greetingTitle}
              greetingSubTitle={greetingSubTitle}
              onBack={handleBack}
              onToggleChildrenMode={toggleSessionChildrenMode}
              onCategoryClick={handleCategoryClick}
            />
          )}
        </main>
        <footer>
          <div className={classes.normalModeFooter}>
            {tickerText && (
              <div className={classes.tickerMarquee}>
                <div className={classes.tickerText}>{tickerText}</div>
                <div className={classes.tickerText} aria-hidden="true">
                  {tickerText}
                </div>
              </div>
            )}
          </div>
        </footer>
      </div>
      {switchByAgeEnabled && !isPreviewMode && (
        <video ref={videoRef} style={{ display: "none" }} muted playsInline />
      )}
      {scanTarget && (
        <TerminalMisScanModal
          language={language}
          target={scanTarget}
          allowsDigitalCode={allowsDigitalCode}
          allowsBarcode={allowsBarcode}
          localTest={localTest}
          isSubmitting={isConfirming}
          errorMessage={scanError}
          onCancel={() => {
            setPendingExtraData(undefined);
            closeScan();
          }}
          onSubmit={async (value) => {
            const result = await submitScan(value);
            if (
              result?.target.scanOnService &&
              result.target.topicId != null
            ) {
              const extraData = pendingExtraData;
              setPendingExtraData(undefined);
              setActiveTicketId(result.target.topicId);
              getTicket({
                topicId: result.target.topicId,
                confirmationId: result.confirmation.confirmationId,
                extraData,
              });
            }
          }}
          onSkipBenefit={
            scanTarget.scanOnService && scanTarget.topicId != null
              ? () => {
                  const topicId = scanTarget.topicId;
                  const extraData = pendingExtraData;
                  setPendingExtraData(undefined);
                  closeScan();
                  if (topicId == null) return;
                  requestTicket(topicId, extraData);
                }
              : undefined
          }
        />
      )}
      {extraInputTarget && (
        <TerminalExtraDataModal
          language={language}
          description={extraInputTarget.extraInputDescription}
          settings={extraInputTarget}
          onCancel={() => {
            setPendingExtraData(undefined);
            setExtraInputTarget(null);
          }}
          onSubmit={(value) => {
            const topicId = extraInputTarget.id;
            setExtraInputTarget(null);
            if (
              !confirmation &&
              orderUnitsData?.misConfig?.benefitsTerminalMode ===
                "SCAN_ON_SERVICE" &&
              orderUnitsData?.misConfig?.benefitsEffective
            ) {
              setPendingExtraData(value);
              openScan({
                privilegeKind: "BENEFIT",
                name: getTicketName(extraInputTarget, language),
                scanCard: true,
                scanOnService: true,
                topicId,
              });
              return;
            }
            requestTicket(topicId, value);
          }}
        />
      )}
      {isScreensaverActive && (
        <div
          className={classes.screensaverLayer}
          onClick={dismissScreensaver}
          role="presentation"
        >
          <TerminalScreensaver
            media={screensaverMedia}
            imageSlideSeconds={screensaverImageSeconds}
          />
        </div>
      )}
    </div>
  );
};

export default NormalMode;
